import { fetchHeldFileVariantBlob, fetchItemAttachmentVariantBlob } from '../services/attachments';
import { normalizeId } from './ids';
import { onSessionChange } from './session';

/**
 * The server-made sizes of an attachment (keepr-api PR 10a): `sm` 160 px
 * square, `md` 800 px, `lg` 2,048 px, WebP. A photo gets all three; a PDF's
 * first page gets `sm` and `md`. `image` is non-null only for a photo, so a
 * PDF is never treated as one, whatever its variants.
 */
export const hasAttachmentVariant = (attachment, size) =>
	Array.isArray(attachment?.variants) && attachment.variants.includes(size);

export const isPhotoAttachment = attachment =>
	Boolean(attachment?.image) && typeof attachment.image === 'object';

/**
 * One object URL per (attachment, size), shared by every surface that shows
 * it, so a list that re-renders (or remounts) does not ask again.
 *
 * A URL is HELD while something shows it (`acquire` … `release`) and is never
 * revoked while held. Released URLs stay ready, the least recently used first
 * in line, until a size has more idle URLs than its limit below; then the
 * oldest idle ones are revoked and forgotten. The limits follow the weights:
 * a `sm` is 6–15 KB and a list shows dozens, an `lg` is up to 600 KB and the
 * viewer shows one at a time.
 *
 * Variant bytes never change for an id, so nothing goes stale. What resets
 * the cache is a different account signing in (it was fetched with this
 * account's token) and an attachment being removed.
 */
const IDLE_LIMITS = { sm: 200, md: 24, lg: 4 };

// `${attachmentId}:${size}` -> { size, attachmentId, holders, url, promise, dropped }
// Map order is recency: every acquire re-inserts its entry at the end.
const cache = new Map();

// A size the server said it does not have (404): not asked again this
// session. Any other failure is forgotten, so the next mount retries.
const missing = new Set();

const keyOf = (attachmentId, size) => `${attachmentId}:${size}`;

const revoke = (entry) => {
	if (!entry.url) return;
	URL.revokeObjectURL(entry.url);
	entry.url = null;
};

function trim(size) {
	const limit = IDLE_LIMITS[size] ?? 0;
	const idle = [...cache.entries()].filter(([, entry]) =>
		entry.size === size && entry.holders === 0 && entry.url);
	let excess = idle.length - limit;
	for (const [key, entry] of idle) {
		if (excess <= 0) break;
		cache.delete(key);
		revoke(entry);
		excess -= 1;
	}
}

/** The URL already in hand, for a first render that should not flash the chip. */
export function peekAttachmentVariantUrl(attachmentId, size) {
	return cache.get(keyOf(normalizeId(attachmentId), size))?.url || null;
}

/**
 * Hold the object URL of one variant. Returns `{ promise, release }`: the
 * promise settles to the URL (or rejects), and `release` must be called once
 * the URL is no longer shown. Releasing twice is harmless.
 */
export function acquireAttachmentVariantUrl(itemId, attachmentId, size) {
	const id = normalizeId(attachmentId);
	return acquire(id, size, () => fetchItemAttachmentVariantBlob(normalizeId(itemId), id, size));
}

/**
 * The same, for a HELD file (an upload the item form has not saved yet),
 * through the held route — its uploader's only (PR 10b). One cache for both
 * routes, keyed by the file id: a held file keeps its id when the save
 * claims it, and its bytes never change, so the thumbnail the form showed is
 * the one the saved item shows.
 */
export function acquireHeldFileVariantUrl(collectionId, fileId, size) {
	const id = normalizeId(fileId);
	return acquire(id, size, () => fetchHeldFileVariantBlob(normalizeId(collectionId), id, size));
}

function acquire(id, size, fetchBlob) {
	const key = keyOf(id, size);
	if (missing.has(key)) {
		return { promise: Promise.reject(new Error('variant_not_found')), release() {} };
	}
	let entry = cache.get(key);
	if (entry) {
		cache.delete(key);
	} else {
		entry = { size, attachmentId: id, holders: 0, url: null, dropped: false };
		const created = entry;
		created.promise = fetchBlob().then(
			(blob) => {
				const url = URL.createObjectURL(blob);
				if (created.dropped && created.holders === 0) {
					URL.revokeObjectURL(url);
					return null;
				}
				created.url = url;
				if (!created.dropped) trim(size);
				return url;
			},
			(error) => {
				if (cache.get(key) === created) cache.delete(key);
				if (error?.response?.status === 404) missing.add(key);
				throw error;
			}
		);
	}
	cache.set(key, entry);
	entry.holders += 1;

	const held = entry;
	let released = false;
	return {
		promise: held.promise,
		release() {
			if (released) return;
			released = true;
			held.holders -= 1;
			if (held.holders > 0) return;
			if (held.dropped) revoke(held);
			else trim(size);
		},
	};
}

// Drop entries: idle URLs are revoked now, held ones when their last holder
// lets go (an image still on screen must not break under it).
function drop(predicate) {
	[...cache.entries()].forEach(([key, entry]) => {
		if (!predicate(entry)) return;
		cache.delete(key);
		entry.dropped = true;
		if (entry.holders === 0) revoke(entry);
	});
}

/** A removed attachment's sizes go with it. */
export function forgetAttachmentVariants(attachmentId) {
	const id = normalizeId(attachmentId);
	drop(entry => entry.attachmentId === id);
	[...missing].forEach((key) => { if (key.startsWith(`${id}:`)) missing.delete(key); });
}

/** Everything, for a new account (and the tests). */
export function clearAttachmentVariantCache() {
	drop(() => true);
	missing.clear();
}

onSessionChange(() => clearAttachmentVariantCache());
